import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController, NavParams } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { PRIVATE_CHANNEL_NAME } from 'src/app/constants';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { getFiltersFromWhereClauses, getWhereClausesFromFilters } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { Profile, SlackChannel, SlackUserGroup, WhereClause } from 'src/models';
import { UserGroup, UserGroupSourceType } from 'src/models/user-groups';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SlackService } from 'src/services/slack.service';
import { UserGroupsService } from 'src/services/user-groups.service';
import { UsersService } from 'src/services/users.service';

import { CreateSlackUserGroupModalComponent } from '../../components/create-slack-user-group-modal/create-slack-user-group-modal.component';
import { CustomFieldsModule } from '../../components/custom-fields/custom-fields.module';

@Component({
  standalone: true,
  selector: 'app-edit-user-group',
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    NgxTippyModule,
    FilterButtonComponent,
    NgxDatatableModule,
    SharedDirectivesModule,
    AvatarModule,
  ],
  templateUrl: './edit-user-group.component.html',
  styleUrls: ['./edit-user-group.component.scss'],
})
export class EditUserGroupComponent implements OnInit {
  @ViewChild('nameInput', { static: false }) nameInput;
  @Input() projectId: string | null = null;

  isLoading = false;
  userGroup?: UserGroup;
  name: string;
  description: string;
  id?: string;
  isDisabled = false;
  sourceType?: UserGroupSourceType;
  sourceId?: string;
  targetId?: string;
  filters: SelectedFilterMap = new Map();
  lookups: Record<string, Record<string, string>> = {};
  isExternal: boolean = false;
  photo?: string;
  slackId?: string;
  filterOptions: FilterOption[] = [
    {
      label: 'Role',
      id: 'role',
      type: 'select',
      searchablePopover: 'tenantUserRoles',
      searchablePopoverOptions: {
        includeOwner: true,
      },
      values: [
        {
          label: 'Owner',
          id: 'owner',
        },
        {
          label: 'Admin',
          id: 'admin',
        },
        {
          label: 'Standard',
          id: 'standard',
        },
      ],
      lookupId: 'role',
    },
    {
      label: 'Slack Channel',
      id: 'slackChannels.id',
      type: 'select',
      searchablePopover: 'slackChannels',
      lookupId: 'slackChannelId',
    },
    {
      label: 'Slack Group',
      id: 'slackGroups.id',
      type: 'select',
      searchablePopover: 'slackGroups',
      lookupId: 'slackGroupId',
      searchablePopoverOptions: {
        excludeManagedByUnthread: true, // Do not allow selecting unthread-managed slack groups in filter criteria
      },
    },
    {
      label: 'SCIM Group',
      id: 'scimGroups.id',
      type: 'select',
      searchablePopover: 'scimGroups',
      lookupId: 'scimGroupId',
    },
    {
      label: 'User',
      id: 'id',
      type: 'select',
      searchablePopover: 'allUsers',
      lookupId: 'assigneeId',
    },
    {
      label: 'Title',
      id: 'title',
      type: 'select',
      searchablePopover: 'dynamicUserFieldValues',
      searchablePopoverOptions: {
        field: 'title',
      },
    },
    {
      label: 'Department',
      id: 'department',
      type: 'select',
      searchablePopover: 'dynamicUserFieldValues',
      searchablePopoverOptions: {
        field: 'department',
      },
    },
    {
      label: 'Division',
      id: 'division',
      type: 'select',
      searchablePopover: 'dynamicUserFieldValues',
      searchablePopoverOptions: {
        field: 'division',
      },
    },
    {
      label: 'Organization',
      id: 'organization',
      type: 'select',
      searchablePopover: 'dynamicUserFieldValues',
      searchablePopoverOptions: {
        field: 'organization',
      },
    },
    {
      label: 'Country',
      id: 'country',
      type: 'select',
      searchablePopover: 'dynamicUserFieldValues',
      searchablePopoverOptions: {
        field: 'country',
      },
    },
    {
      label: 'Region',
      id: 'region',
      type: 'select',
      searchablePopover: 'dynamicUserFieldValues',
      searchablePopoverOptions: {
        field: 'region',
      },
    },
    {
      label: 'City',
      id: 'locality',
      type: 'select',
      searchablePopover: 'dynamicUserFieldValues',
      searchablePopoverOptions: {
        field: 'locality',
      },
    },
    this.authService.tenant.integrations?.pagerDuty?.enabled
      ? {
          label: 'PagerDuty Escalation Policy',
          id: 'pagerDutyEscalationPolicies.id',
          type: 'select',
          searchablePopover: 'pagerDutyEscalationPolicies',
          lookupId: 'pagerDutyEscalationPolicyId',
        }
      : undefined,
  ].filter((f) => f !== undefined) as FilterOption[];

  isLoadingUsers = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;

  slackChannel?: {
    id: string;
    name: string;
  };

  slackUserGroup?: SlackUserGroup;

  sortField = 'name';
  sortDesc = false;
  limit = 10;
  searchText: string;

  fetchUsers = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<Profile>>> => {
    const where: WhereClause[] = getWhereClausesFromFilters(this.filters);

    if (this.sourceType !== 'system' && !where.length) {
      return {
        data: [],
        totalCount: 0,
        cursors: {
          hasNext: false,
          hasPrevious: false,
        },
      };
    }

    const options: any = {
      limit,
      order,
      descending,
      cursor,
      includeExternalUsers: true,
    };

    where.push({
      field: 'isExternal',
      operator: '==',
      value: this.isExternal ?? false,
    });

    if (where.length) {
      options.where = where;
    }

    const result = await this.usersService.list(options);

    return result;
  };

  usersDataSource = createDataSource(this.fetchUsers);

  constructor(
    navParams: NavParams,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private userGroupsService: UserGroupsService,
    private apiService: ApiService,
    private usersService: UsersService,
    private slackService: SlackService,
  ) {
    this.id = navParams.get('id');
  }

  async ngOnInit() {
    if (this.id) {
      await this.loadUserGroup();
    } else {
      this.isDisabled = !this.canEditUserGroup(this.projectId);

      if (this.isDisabled) {
        this.msgSrvc.showError('You do not have permission to create a user group for this project');
        this.modalCtrl.dismiss();
        return;
      }

      setTimeout(() => this.nameInput.setFocus(), 500);
    }

    this.loadUsers();
  }

  async loadUserGroup() {
    this.isLoading = true;

    try {
      const userGroup = await this.userGroupsService.getById(this.id);
      this.userGroup = userGroup;

      // Permission check: verify user can edit this user group
      if (!this.canEditUserGroup(userGroup.projectId)) {
        this.msgSrvc.showError('You do not have permission to edit this user group');
        this.modalCtrl.dismiss();
        return;
      }

      // Set isDisabled based on permissions for this specific user group
      this.isDisabled = !this.canEditUserGroup(userGroup.projectId);

      this.name = userGroup.name;
      this.description = userGroup.description;
      this.slackId = userGroup.slackId;
      this.sourceType = userGroup.sourceType;
      this.sourceId = userGroup.sourceId;
      this.targetId = userGroup.targetId;
      this.isExternal = userGroup.isExternal;
      this.photo = userGroup.photo;
      this.projectId = userGroup.projectId;

      const lookupIds: Record<string, string[]> = this.slackId
        ? {
            slackChannelId: [this.slackId],
          }
        : {};

      for (const filter of userGroup.filters ?? []) {
        const filterOption = this.filterOptions.find((o) => o.id === filter.field);

        if (!filterOption?.lookupId) {
          continue;
        }

        if (!lookupIds[filterOption.lookupId]) {
          lookupIds[filterOption.lookupId] = [];
        }

        const values = Array.isArray(filter.value) ? filter.value : [filter.value];

        lookupIds[filterOption.lookupId].push(...values);
      }

      this.lookups = await this.apiService.postPromise('/saved-filters/get-lookup-values', lookupIds);

      if (this.slackId) {
        this.slackChannel = {
          id: this.slackId,
          name: this.lookups.slackChannelId[this.slackId] ?? PRIVATE_CHANNEL_NAME,
        };
      }

      this.filters = getFiltersFromWhereClauses(userGroup.filters ?? [], this.filterOptions, this.lookups);

      if (this.targetId) {
        try {
          this.slackUserGroup = await this.slackService.getSlackUserGroupBySlackId(this.targetId);
        } catch (error) {
          console.warn('Could not fetch Slack user group details', error);
        }
      }

      if (this.sourceType) {
        this.isDisabled = true;
      }
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async loadUsers() {
    this.isLoadingUsers = true;

    await this.usersDataSource.firstPage();

    this.isLoadingUsers = false;
  }

  async submit() {
    if (!this.name) {
      await this.msgSrvc.show('Whoops! Please enter a name.');

      return;
    }

    this.isLoading = true;

    try {
      if (this.id) {
        await this.userGroupsService.updatePartial(this.id, {
          name: this.name,
          description: this.description,
          filters: getWhereClausesFromFilters(this.filters),
          photo: this.photo,
          slackId: this.slackId,
          projectId: this.projectId,
        });

        await this.msgSrvc.show('User group updated!');
      } else {
        await this.userGroupsService.create({
          name: this.name,
          description: this.description,
          filters: getWhereClausesFromFilters(this.filters),
          isExternal: this.isExternal,
          photo: this.photo,
          slackId: this.slackId,
          projectId: this.projectId,
        });

        await this.msgSrvc.show('UserGroup added!');
      }

      this.dismiss(this.id ? 'update' : 'add');
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete group?',
      subHeader: `This can't be undone.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.userGroupsService.delete(this.id);
              this.isLoading = false;
              this.dismiss('delete');
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.show(`Unable to delete userGroup: ${e}`);
            }
          },
        },
      ],
    });
    alert.present();
  }

  dismiss(action?: 'delete' | 'update' | 'add') {
    this.modalCtrl.dismiss({ action });
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;

    this.loadUsers();
  }

  isExternalDidChange() {
    this.loadUsers();
  }

  onSlackChannelChange(channel: SlackChannel) {
    this.slackId = channel.id;
    this.slackChannel = {
      id: channel.id,
      name: channel.name,
    };
  }

  get showUserGroupToSlackGroupSyncSettings(): boolean {
    return this.filters.size > 0 && this.userGroupsService.canSyncToSlack(this.userGroup);
  }

  async openCreateSlackUserGroupModal() {
    const modal = await this.modalCtrl.create({
      component: CreateSlackUserGroupModalComponent,
      cssClass: 'modal-content-only modal-skinny',
      backdropDismiss: true,
      componentProps: {
        userGroupId: this.id,
        userGroupName: this.name,
        userGroupDescription: this.description,
        connectionId: this.authService.tenant.slackTeamId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(async ({ data }) => {
      if (data?.success) {
        await this.loadUserGroup();
        this.msgSrvc.show('Slack user group created successfully! Users will sync to this group in the background.');
      }
    });
  }

  async disableSlackUserGroup() {
    try {
      await this.slackService.disableLinkedSlackUserGroup(this.id);

      // Clear the linked state immediately
      this.targetId = undefined;
      this.slackUserGroup = undefined;

      this.msgSrvc.show('Slack user group sync disabled.');
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  canEditUserGroup(projectId: string | null): boolean {
    // Tenant admins can edit any user group
    if (this.authService.isTenantAdmin) {
      return true;
    }

    // Project owners can only edit user groups for their own project
    if (projectId) {
      return this.authService.isProjectOwner(projectId);
    }

    // Tenant-level groups (projectId = null) can only be edited by tenant admins
    return false;
  }
}
